import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { ShieldAlert, Plus, Trash2, Upload, FileCheck2, Lock } from "lucide-react";

const REASONS = [
    ["restraining_order", "Restraining / No-Contact Order"],
    ["safety_concern", "Safety Concern"],
    ["former_partner", "Former Partner"],
    ["other", "Other"],
];
const REASON_LABEL = Object.fromEntries(REASONS);
const STATUS = {
    confirmed: { label: "Active block", cls: "bg-primary/10 text-primary" },
    review: { label: "Under review", cls: "bg-amber-100 text-amber-700" },
    no_match: { label: "Saved — watching for a match", cls: "bg-secondary text-muted-foreground" },
};

const EMPTY = { full_name: "", middle_name: "", nickname: "", approx_age: "", dob: "", city: "", email: "", phone: "", reason: "" };

export default function SafetyExclusions() {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(false);
    const load = () => api.get("/me/do-not-group").then((r) => setData(r.data)).catch(() => {});
    useEffect(() => { load(); }, []);
    const del = async (id) => {
        if (!window.confirm("Remove this person from your Do-Not-Group list?")) return;
        try { await api.delete(`/me/do-not-group/${id}`); toast.success("Removed."); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    return (
        <div className="rounded-2xl bg-card p-6 shadow-sm" data-testid="safety-privacy-card">
            <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary"><ShieldAlert className="h-5 w-5" /></div>
                <div className="flex-1">
                    <h3 className="font-display text-lg font-700 text-primary">Safety &amp; Privacy — My Do-Not-Group List</h3>
                    <p className="mt-1 text-sm text-muted-foreground">Privately list anyone you should never be placed in an activity group with — an ex, a no-contact order, or any personal safety concern. This stays private: the other person is never told, and it works even if only you request it.</p>
                </div>
            </div>

            <div className="mt-5 space-y-2" data-testid="dng-list">
                {data?.exclusions?.length ? data.exclusions.map((e) => (
                    <div key={e.id} className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3" data-testid={`dng-entry-${e.id}`}>
                        <div className="min-w-0">
                            <p className="truncate font-600 text-primary">{e.full_name}{e.nickname ? ` (${e.nickname})` : ""}</p>
                            <p className="text-xs text-muted-foreground">{REASON_LABEL[e.reason] || e.reason}{e.city ? ` · ${e.city}` : ""}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-600 ${STATUS[e.status]?.cls}`}>{STATUS[e.status]?.label || e.status}</span>
                            <button onClick={() => del(e.id)} data-testid={`dng-delete-${e.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                        </div>
                    </div>
                )) : <p className="text-sm text-muted-foreground">No one on your list yet.</p>}
            </div>

            {data?.outing_block_count > 0 && (
                <p className="mt-3 flex items-center gap-2 rounded-xl bg-secondary/60 px-3 py-2 text-xs text-muted-foreground" data-testid="dng-outing-note">
                    <Lock className="h-3.5 w-3.5 shrink-0" /> You've also asked not to be grouped with {data.outing_block_count} {data.outing_block_count === 1 ? "person" : "people"} you met on outings. Those blocks stay private and active too.
                </p>
            )}

            <AddDialog open={open} setOpen={setOpen} onSaved={load} />
        </div>
    );
}

function AddDialog({ open, setOpen, onSaved }) {
    const [f, setF] = useState(EMPTY);
    const [photo, setPhoto] = useState(null);
    const [doc, setDoc] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
    const submit = async () => {
        if (!f.full_name.trim()) { toast.error("Enter the person's full name."); return; }
        if (!f.reason) { toast.error("Choose a reason."); return; }
        setBusy(true);
        try {
            const fd = new FormData();
            Object.entries(f).forEach(([k, v]) => fd.append(k, v));
            if (photo) fd.append("photo", photo);
            if (doc) fd.append("document", doc);
            const { data } = await api.post("/me/do-not-group", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const msg = data.status === "confirmed" ? "Added — this person will never be grouped with you."
                : data.status === "review" ? "Added — our team will review a possible match to finish setting up the block."
                    : "Saved privately — we'll apply the block if this person is a Single Social Club member.";
            toast.success(msg);
            setF(EMPTY); setPhoto(null); setDoc(null); setOpen(false); onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid="dng-add-open" variant="outline" className="mt-4 rounded-full"><Plus className="mr-1 h-4 w-4" /> Add someone to my Do-Not-Group list</Button>
            </DialogTrigger>
            <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="dng-add-dialog">
                <DialogHeader>
                    <DialogTitle>Add someone to your Do-Not-Group list</DialogTitle>
                    <DialogDescription>Share whatever you know — even a name helps. Everything here is private and never shown to the other person. You don't need to explain the details.</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <label className="text-sm font-600">Full legal name (including middle name if known) *</label>
                        <Input data-testid="dng-full-name" value={f.full_name} onChange={set("full_name")} placeholder="Jane Marie Doe" className="mt-1" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-sm font-600">Nickname / other name</label><Input data-testid="dng-nickname" value={f.nickname} onChange={set("nickname")} className="mt-1" /></div>
                        <div><label className="text-sm font-600">City</label><Input data-testid="dng-city" value={f.city} onChange={set("city")} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-sm font-600">Approx. age</label><Input data-testid="dng-age" value={f.approx_age} onChange={set("approx_age")} placeholder="e.g. 34" className="mt-1" /></div>
                        <div><label className="text-sm font-600">Date of birth (if known)</label><Input data-testid="dng-dob" type="date" value={f.dob} onChange={set("dob")} className="mt-1" /></div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div><label className="text-sm font-600">Email</label><Input data-testid="dng-email" value={f.email} onChange={set("email")} className="mt-1" /></div>
                        <div><label className="text-sm font-600">Phone</label><Input data-testid="dng-phone" value={f.phone} onChange={set("phone")} className="mt-1" /></div>
                    </div>
                    <div>
                        <label className="text-sm font-600">Reason *</label>
                        <Select value={f.reason} onValueChange={(v) => setF((s) => ({ ...s, reason: v }))}>
                            <SelectTrigger data-testid="dng-reason" className="mt-1"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                            <SelectContent>{REASONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-sm font-600">Photo (optional)</label>
                            <label htmlFor="dng-photo" className="mt-1 flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground hover:border-primary">
                                {photo ? <FileCheck2 className="h-4 w-4 text-primary" /> : <Upload className="h-4 w-4" />}<span className="truncate">{photo ? photo.name : "Add a photo"}</span>
                            </label>
                            <input id="dng-photo" data-testid="dng-photo-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={(e) => setPhoto(e.target.files?.[0] || null)} className="hidden" />
                        </div>
                        <div>
                            <label className="text-sm font-600">Supporting document (optional)</label>
                            <label htmlFor="dng-doc" className="mt-1 flex cursor-pointer items-center gap-2 rounded-xl border border-dashed border-border px-3 py-2.5 text-xs text-muted-foreground hover:border-primary">
                                {doc ? <FileCheck2 className="h-4 w-4 text-primary" /> : <Upload className="h-4 w-4" />}<span className="truncate">{doc ? doc.name : "Court order, etc."}</span>
                            </label>
                            <input id="dng-doc" data-testid="dng-doc-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,application/pdf" onChange={(e) => setDoc(e.target.files?.[0] || null)} className="hidden" />
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="ghost" onClick={() => setOpen(false)} data-testid="dng-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={busy} data-testid="dng-submit" className="rounded-full bg-primary hover:bg-primary/90">{busy ? "Saving…" : "Add to my list"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
